import flowRight from 'lodash/flowRight';
import identity from 'lodash/identity';
import mergeWith from 'lodash/mergeWith';
import pick from 'lodash/pick';
import React, { ReactElement, Ref, useMemo, useRef } from 'react';
import ReactSelect, { createFilter, components as SelectComponents } from 'react-select';
import { ContainerProps } from 'react-select/lib/components/containers';
import Creatable, { Props as CreatableProps } from 'react-select/lib/Creatable';
import { Styles } from 'react-select/lib/styles';
import { ThemeConfig } from 'react-select/lib/theme';
import { devWarning, OmitStrict, TestIdProps, UnsafeSpreadProps } from '@tanium/coreui-utils';
import { useTheme } from '@tanium/react-theme-context';
import { getComponents } from './getComponents';
import { getStyles } from './getStyles';
import { useTranslation } from './localization';
import { SingleLineOption } from './SingleLineOption';
import { SelectSize } from './types';

export const selectPropsAllowList = [
  'aria-label',
  'aria-labelledby',
  'autoFocus',
  'backspaceRemovesValue',
  'blurInputOnSelect',
  'closeMenuOnSelect',
  'components',
  'defaultInputValue',
  'defaultMenuIsOpen',
  'defaultValue',
  'delimiter',
  'filterOption',
  'formatGroupLabel',
  'formatOptionLabel',
  'getOptionLabel',
  'getOptionValue',
  'hideSelectedOptions',
  'id',
  'inputId',
  'instanceId',
  'isClearable',
  'isDisabled',
  'isLoading',
  'isMulti',
  'isOptionDisabled',
  'isOptionSelected',
  'isSearchable',
  'loadingMessage',
  'maxMenuHeight',
  'menuIsOpen',
  'menuPlacement',
  'menuPortalTarget',
  'menuPosition',
  'minMenuHeight',
  'name',
  'noOptionsMessage',
  'onBlur',
  'onChange',
  'onFocus',
  'onInputChange',
  'onKeyDown',
  'onMenuClose',
  'onMenuOpen',
  'onMenuScrollToBottom',
  'onMenuScrollToTop',
  'openMenuOnClick',
  'openMenuOnFocus',
  'options',
  'pageSize',
  'placeholder',
  'screenReaderStatus',
  'styles',
  'tabIndex',
  'value',
] as const;

export const combinedPropAllowList = [
  'formatCreateLabel',
  'isValidNewOption',
  'getNewOptionData',
  'onCreateOption',
  'createOptionPosition',
  ...selectPropsAllowList,
] as const;

type AllowedProps = typeof combinedPropAllowList[number];

// eslint-disable-next-line @typescript-eslint/ban-types -- preserving {} for backwards compatibility
export type SelectType<T = {}> = ReactSelect<T> | Creatable<T>;

export interface Props<T>
  extends Pick<CreatableProps<T>, AllowedProps>,
    TestIdProps,
    UnsafeSpreadProps {
  /**
   * The id to set on the SelectContainer component.
   */
  containerId?: string;
  /**
   * The id to set on the SelectContainer component.
   *
   * @deprecated This id is attached to the `Select`'s container component, not
   * its `input` element. Use `containerId` or `inputId` instead.
   */
  id?: string;
  /**
   * Whether or not new options can be created on the fly.
   */
  isCreatable?: boolean;
  /**
   * Whether or not to collapse multiple items to fit into a single line
   * when the select is not disabled or the menu is not open. When this is true,
   * it will try to prevent the presence of multiple pills from causing the select
   * to wrap to more than one line.
   */
  isMultiCollapsible?: boolean;
  /**
   * Ref for the underlying component.
   */
  innerRef?: Ref<ReactSelect<T> | Creatable<T>>;
  /**
   * Whether the select component should be displayed in an invalid (error) state.
   */
  invalid?: boolean;
  /**
   * Whether the option text is wrapped or not. If a custom
   * Option component is passed, then this property is ignored.
   * Defaults to `false`
   */
  wrapOptionText?: boolean;

  /**
   * Size of the select component. Use `sm` or `lg`. Defaults to `lg`.
   */
  size?: SelectSize;
  /**
   * whether limit the max width of selected multi-value pills to be 200. Defaults to be `true`.
   * When `false`, long pills will grow to fullfil its parent container's width.
   */
  enablePillsMaxWidth?: boolean;
}

const combineStyles = (selectStyles: Partial<Styles>, customStyles = {} as Partial<Styles>) =>
  mergeWith(selectStyles, customStyles, (base = identity, custom = identity) =>
    flowRight(custom, base),
  );

const createSelectContainer =
  <T extends unknown>(testId?: string) =>
  ({ innerProps, ...props }: ContainerProps<T>) => {
    const innerPropsWithTestId = useMemo(
      () => ({ ...innerProps, ...(testId && { 'data-test-id': testId }) }),
      [innerProps],
    );
    return <SelectComponents.SelectContainer {...props} innerProps={innerPropsWithTestId} />;
  };

const filterOption = createFilter({ ignoreAccents: false });

/**
 * Styled and Tanium-friendly wrapper around a react-select component. See
 * https://react-select.com/home for usage.
 */
const SelectInner = <T extends unknown>({
  blurInputOnSelect = true,
  'data-test-id': testId,
  wrapOptionText = false,
  size = 'lg',
  isMultiCollapsible = false,
  id,
  containerId = id,
  ...props
}: Props<T>): JSX.Element => {
  const isInitiallyCreatable = useRef(!!props.isCreatable);
  const { t } = useTranslation();

  devWarning(
    !!props.isCreatable === !!isInitiallyCreatable.current,
    `A Select component was initialized with isCreatable as ${isInitiallyCreatable.current}, but now isCreatable is ${props.isCreatable}. Toggling this prop yields undefined behavior. Do not toggle isCreatable on a component; instead completely unmount it and remount a new component.`,
  );

  const allProps = { blurInputOnSelect, ...(containerId && { id: containerId }), ...props };

  const theme = useTheme();
  const selectStyles = useMemo(
    () => getStyles({ theme, size, invalid: props.invalid }),
    [theme, size, props.invalid],
  );

  const SelectContainer = useMemo(() => createSelectContainer<T>(testId), [testId]);

  const commonProps = useMemo(
    () => ({
      styles: combineStyles(selectStyles, props.styles),
      components: {
        SelectContainer,
        ...getComponents({
          size,
          isMultiCollapsible,
          enablePillsMaxWidth: props.enablePillsMaxWidth,
        }),
        ...(!wrapOptionText && { Option: SingleLineOption }),
        ...props.components,
      },
      filterOption,
      theme: ((defaultTheme) => ({
        ...defaultTheme,
        borderRadius: theme.radii[1],
        spacing: {
          ...defaultTheme.spacing,
          controlHeight: 32,
        },
      })) as ThemeConfig,
      // eslint-disable-next-line no-underscore-dangle
      ...props.__UNSAFE_ADDITIONAL_PROPS__,
    }),
    [
      selectStyles,
      props.styles,
      props.enablePillsMaxWidth,
      props.components,
      // eslint-disable-next-line no-underscore-dangle
      props.__UNSAFE_ADDITIONAL_PROPS__,
      SelectContainer,
      size,
      isMultiCollapsible,
      wrapOptionText,
      theme.radii,
    ],
  );

  const translatedProps = useMemo(
    () => ({
      loadingMessage: () => t('Loading...'),
      noOptionsMessage: () => t('No options'),
    }),
    [t],
  );

  const formatCreateLabel = useMemo(
    () => (newOption: string) => t('Create "{{newOption}}"', { newOption }),
    [t],
  );

  if (!props.isCreatable) {
    const selectComponentProps = pick(allProps, selectPropsAllowList);

    return (
      <ReactSelect
        ref={props.innerRef as Ref<ReactSelect<T>>}
        placeholder={t('Select...')}
        {...translatedProps}
        {...selectComponentProps}
        {...commonProps}
      />
    );
  }

  const creatableComponentProps = pick(allProps, combinedPropAllowList);
  return (
    <Creatable
      ref={props.innerRef as Ref<Creatable<T>>}
      placeholder={t('Select or type a new value...')}
      {...translatedProps}
      formatCreateLabel={formatCreateLabel}
      {...creatableComponentProps}
      isSearchable={
        creatableComponentProps.isSearchable !== undefined
          ? true
          : creatableComponentProps.isSearchable
      }
      {...commonProps}
    />
  );
};

// eslint-disable-next-line @typescript-eslint/ban-types -- preserving {} for backwards compatibility
const RefSelectComp = <T extends unknown = {}>(
  props: OmitStrict<Props<T>, 'innerRef'>,
  ref: Ref<SelectType<T>>,
) => <SelectInner innerRef={ref} {...props} />;

// eslint-disable-next-line @typescript-eslint/ban-types -- preserving {} for backwards compatibility
export const Select = React.forwardRef(RefSelectComp) as <T extends unknown = {}>(
  p: Props<T> & { ref?: Ref<SelectType<T>> },
) => ReactElement;
